import React, { useState, useEffect } from 'react'
import { superadminAPI } from '../../services/api'
import { useAlerts } from '../../contexts/AlertContext'
import {
  HiOutlineMegaphone,
  HiOutlineCreditCard,
  HiOutlineEnvelope,
  HiOutlinePaperAirplane,
  HiOutlineUsers,
  HiOutlineBriefcase,
  HiOutlineGlobeAlt,
  HiOutlineCheckCircle
} from 'react-icons/hi2'

const CommunicationsPage = () => {
  const { showSuccess, showError } = useAlerts()
  const [activeTab, setActiveTab] = useState('broadcast')
  
  // Broadcast state
  const [broadcastData, setBroadcastData] = useState({
    title: '',
    message: '',
    target: 'all'
  })
  const [broadcastLoading, setBroadcastLoading] = useState(false)

  // Contact messages state
  const [contactMessages, setContactMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messageFilters, setMessageFilters] = useState({
    category: '',
    status: ''
  })
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [replyText, setReplyText] = useState('')
  const [replyLoading, setReplyLoading] = useState(false)

  useEffect(() => {
    if (activeTab === 'contact') {
      fetchContactMessages()
    }
  }, [activeTab, messageFilters])

  const fetchContactMessages = async () => {
    try {
      setMessagesLoading(true)
      const response = await superadminAPI.getContactMessages(messageFilters)
      
      if (response.success) {
        setContactMessages(response.data.data || [])
      } else {
        showError('Failed to load contact messages')
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
      showError('Failed to load contact messages')
    } finally {
      setMessagesLoading(false)
    }
  }

  const handleBroadcast = async () => {
    if (!broadcastData.title || !broadcastData.message) {
      showError('Title and message are required')
      return
    }

    try {
      setBroadcastLoading(true)
      const response = await superadminAPI.broadcastNotification(broadcastData)
      
      if (response.success) {
        showSuccess(`Notification sent to ${response.data.data.recipient_count} user(s)`)
        setBroadcastData({ title: '', message: '', target: 'all' })
      } else {
        showError('Failed to send broadcast')
      }
    } catch (error) {
      console.error('Error sending broadcast:', error)
      showError('Failed to send broadcast')
    } finally {
      setBroadcastLoading(false)
    }
  }

  const handlePaymentReminder = async () => {
    try {
      const response = await superadminAPI.sendPaymentReminders()
      
      if (response.success) {
        showSuccess(`Payment reminders sent to ${response.data.data.reminder_count} user(s)`)
      } else {
        showError('Failed to send reminders')
      }
    } catch (error) {
      console.error('Error sending reminders:', error)
      showError('Failed to send reminders')
    }
  }

  const handleReply = async () => {
    if (!selectedMessage || !replyText) {
      showError('Reply text is required')
      return
    }

    try {
      setReplyLoading(true)
      const response = await superadminAPI.replyToContactMessage(
        selectedMessage.id,
        replyText
      )
      
      if (response.success) {
        showSuccess('Reply sent successfully')
        setSelectedMessage(null)
        setReplyText('')
        fetchContactMessages()
      } else {
        showError('Failed to send reply')
      }
    } catch (error) {
      console.error('Error sending reply:', error)
      showError('Failed to send reply')
    } finally {
      setReplyLoading(false)
    }
  }

  const getCategoryBadge = (category) => {
    const badges = {
      contact_user: { color: 'bg-blue-100 text-brand', label: 'User Contact' },
      contact_visitor: { color: 'bg-brand-soft text-brand', label: 'Visitor Contact' },
      bug_report: { color: 'bg-red-100 text-danger', label: 'Bug Report' },
      suggestion: { color: 'bg-green-100 text-success', label: 'Suggestion' }
    }
    const badge = badges[category] || { color: 'bg-gray-100 text-gray-700', label: category }
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>{badge.label}</span>
  }

  const getStatusBadge = (status) => {
    const badges = {
      new: { color: 'bg-amber-100 text-warning', label: 'New' },
      in_progress: { color: 'bg-blue-100 text-brand', label: 'In Progress' },
      replied: { color: 'bg-green-100 text-success', label: 'Replied' },
      closed: { color: 'bg-gray-100 text-gray-700', label: 'Closed' }
    }
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-700', label: status }
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>{badge.label}</span>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Communications</h1>
        <p className="text-gray-600 mt-1">Manage notifications and contact messages</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('broadcast')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'broadcast'
                ? 'border-red-500 text-danger'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <HiOutlineMegaphone className="inline w-5 h-5 mr-2" />
            Broadcast Notification
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'payment'
                ? 'border-red-500 text-danger'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <HiOutlineCreditCard className="inline w-5 h-5 mr-2" />
            Payment Reminders
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'contact'
                ? 'border-red-500 text-danger'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <HiOutlineEnvelope className="inline w-5 h-5 mr-2" />
            Contact Messages
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'broadcast' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Send Broadcast Notification</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="all"
                    checked={broadcastData.target === 'all'}
                    onChange={(e) => setBroadcastData({ ...broadcastData, target: e.target.value })}
                    className="mr-2"
                  />
                  <HiOutlineGlobeAlt className="w-5 h-5 mr-1" />
                  All Users
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="owners"
                    checked={broadcastData.target === 'owners'}
                    onChange={(e) => setBroadcastData({ ...broadcastData, target: e.target.value })}
                    className="mr-2"
                  />
                  <HiOutlineBriefcase className="w-5 h-5 mr-1" />
                  Owners Only
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="employees"
                    checked={broadcastData.target === 'employees'}
                    onChange={(e) => setBroadcastData({ ...broadcastData, target: e.target.value })}
                    className="mr-2"
                  />
                  <HiOutlineUsers className="w-5 h-5 mr-1" />
                  Employees Only
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                value={broadcastData.title}
                onChange={(e) => setBroadcastData({ ...broadcastData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Notification title..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                value={broadcastData.message}
                onChange={(e) => setBroadcastData({ ...broadcastData, message: e.target.value })}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Your message here..."
              />
            </div>

            <button
              onClick={handleBroadcast}
              disabled={broadcastLoading || !broadcastData.title || !broadcastData.message}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <HiOutlinePaperAirplane className="w-5 h-5" />
              {broadcastLoading ? 'Sending...' : 'Send Broadcast Notification'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Reminders</h2>
          <p className="text-gray-600 mb-6">
            Send payment reminders to all users with expired subscriptions.
          </p>
          
          <button
            onClick={handlePaymentReminder}
            className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
          >
            <HiOutlinePaperAirplane className="w-5 h-5" />
            Send Payment Reminders
          </button>
        </div>
      )}

      {activeTab === 'contact' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={messageFilters.category}
                  onChange={(e) => setMessageFilters({ ...messageFilters, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">All Categories</option>
                  <option value="contact_user">User Contact</option>
                  <option value="contact_visitor">Visitor Contact</option>
                  <option value="bug_report">Bug Report</option>
                  <option value="suggestion">Suggestion</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={messageFilters.status}
                  onChange={(e) => setMessageFilters({ ...messageFilters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="replied">Replied</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => setMessageFilters({ category: '', status: '' })}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Messages List */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {messagesLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : contactMessages.length === 0 ? (
              <div className="text-center py-12">
                <HiOutlineEnvelope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">No contact messages found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {contactMessages.map((message) => (
                  <div key={message.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{message.subject}</h3>
                          {getCategoryBadge(message.category)}
                          {getStatusBadge(message.status)}
                        </div>
                        <p className="text-sm text-gray-600">
                          From: {message.sender_name_display} ({message.sender_email})
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(message.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-3">{message.message}</p>
                    
                    {message.status === 'replied' && message.reply_text && (
                      <div className="bg-success-soft border-l-4 border-green-500 p-4 mb-3">
                        <p className="text-sm font-medium text-green-900 mb-1">
                          Reply by {message.replied_by_name} on {new Date(message.replied_at).toLocaleString()}
                        </p>
                        <p className="text-sm text-green-800">{message.reply_text}</p>
                      </div>
                    )}
                    
                    {message.status !== 'replied' && (
                      <button
                        onClick={() => setSelectedMessage(message)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        Reply
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reply to Message</h3>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Subject: <span className="font-medium text-gray-900">{selectedMessage.subject}</span></p>
              <p className="text-sm text-gray-600 mb-1">From: <span className="font-medium text-gray-900">{selectedMessage.sender_name_display}</span></p>
              <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-900">{selectedMessage.sender_email}</span></p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Reply</label>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows="6"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Type your reply here..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleReply}
                disabled={replyLoading || !replyText}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <HiOutlinePaperAirplane className="w-5 h-5" />
                {replyLoading ? 'Sending...' : 'Send Reply'}
              </button>
              <button
                onClick={() => {
                  setSelectedMessage(null)
                  setReplyText('')
                }}
                disabled={replyLoading}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CommunicationsPage
